import { progressStart, progressEnd } from '@ibkr/lib.common/src/js/progress'
import IntervalTimer from '@ibkr/clientportal.common/src/js/interval'
import api from '@ibkr/clientportal.common/src/api/iserverApi.js'
import hmdsApi from '@ibkr/clientportal.common/src/api/hmdsApi.js'
import mdApi from '@ibkr/clientportal.common/src/api/mdApi.js'
import Metrics from '@ibkr/clientportal.common/src/js/Metrics'
import realtime from '@ibkr/clientportal.common/src/js/realtime'
import { safeCall, retryReset } from '@ibkr/clientportal.common/src/js/promise'
import { getHttpErrorReason, httpFailed, httpError } from '@ibkr/clientportal.common/src/js/http'
import { authenticateSSODH } from '@ibkr/clientportal.common/src/api/sso'

function genUniqueRandom() {
    return genS() + genS();
}

function genS() {
    return Math.floor((1+ Math.random()) * 0x10000 ).toString(16).substring(1);
}

import * as types from '../../mutation-types'
const machineId = genUniqueRandom();
const bagConidPattern = /.*;.*\//;

let retryAccounts = 0;
let retrySsodh = 0;
let retryGetOrderSnapshot = 0;
let waitDH = false;

let subscribedConids = {};
let ordersPollingHandler = null;
let ordersSnapshotHandler = null;
let pnlPollingHandler = null;
let mdPollingHandler = null;
let authStatusPollingHandler = null;
let tradesPollingHandler = null;

const MAX_SNAPSHOT_RETRIES = 3;
const POLLING_INTERVAL = 3000;

// regsnapshot requests
const SNAPSHOT_FEE_ALERT_LIMIT = 10; // defined in Jts/iserver/scripts/server.properties
const SNAPSHOT_EXCESSIVE_USAGE_LIMIT = 50; // defined in Jts/iserver/scripts/server.properties
const SNAPSHOT_FEE_ALERT = {"id":"SNAPSHOT_FEE_ALERT","text":"We charge a small fee for every Snapshot Market Data request you make.","title":"Cost Reminder","url":"https://www.interactivebrokers.com/en/index.php?f=14193#market-data-fees"};
const SNAPSHOT_EXCESSIVE_USAGE_WARNING = {"id":"SNAPSHOT_EXCESSIVE_USAGE_WARNING","text":"Snapshot market data service is not meant to replace real-time market data subscription.","title":"Snapshot Excessive Usage","url":"https://www.interactivebrokers.com/en/index.php?f=14193#market-data-display"};
let regsnapshotRequestCount = 0;

let accountsFetcher = null; // points to current iserver accounts polling instance
let tradesSubscriptionActive = false;

function doGetAccounts() {
    return new Promise((resolve, reject) => {
        api.getAccounts()
            .then(resolve)
            .catch(reject);
      });
}

function getOrdersSnapshot({ commit }) {
    if (ordersSnapshotHandler != null) ordersSnapshotHandler.stop();
    ordersSnapshotHandler = new IntervalTimer('snapshot orders',
       {
            interval: POLLING_INTERVAL
       },
       () => api.getOrders()
            .then((res) => {
                if (res.orders) {
                    commit(types.SET_ORDERS, res.orders);
                }
                if (res.snapshot) {
                    // reset retry value to 0
                    retryGetOrderSnapshot = 0;
                    ordersSnapshotHandler && ordersSnapshotHandler.stop();
                }
            })
            .catch((result) => {
                // increment retry
                retryGetOrderSnapshot++;
                // stop retrying if request has failed >= 3 times
                if (retryGetOrderSnapshot >= 3) {
                    console.error(`Failed to fetch orders snapshot. Request failed ${retryGetOrderSnapshot}. Stopped polling.`);
                    ordersSnapshotHandler && ordersSnapshotHandler.stop();
                }
                console.error('Failed to get orders snapshot ', result);
            })
    );
    ordersSnapshotHandler.start();
}

//--  needed for price alerts formatting for Garban Price
function formatAlertByRules(rules, alert) {
    if (rules.incrementType === 4) {
        alert.ticks = true;
        alert.increment = rules.incrementRules[0].increment;
        alert.condition_value = $num.parseTicks(alert.conditions[0].condition_value);
    } else {
        alert.condition_value = Number(alert.conditions[0].condition_value);
    }
}

export const getOrderStatus = ({ commit, dispatch, state }, { orderId, onSuccess, onError }) => {
    api.getOrderStatus(orderId)
        .then((res) => {
            safeCall('order-status', onSuccess, res);
    })
    .catch((result) => {
        safeCall('order-status', onError, result);
        httpFailed('get order status', commit, result)
    });
}

export const onIserverAuthStatus = ({ dispatch, commit, state}, payload) => {
    console.debug('on iserver auth status auth', payload);
    const changed = (state.status.authenticated != payload.authenticated || state.status.competing != payload.competing);
    commit(types.SET_AUTH_STATUS, payload);
    if (changed) {
        dispatch('onLoggedIn', payload);
    }
}

export const retrySSODH = ({ dispatch, state, getters, commit }) => {
    console.debug('retry ssodh, authenticated: ', getters.connected);
    waitDH = false;
    if (getters.connected) {
        commit(types.SET_ISERVER_LOGGING_IN_STATUS, false);
        console.debug('retry ssodh: already authenticated');
        return;
    }
    try {
        setTimeout(() => dispatch('startSSODH'), 3000);
    } catch (e) {
        console.debug(e);
    }
}

/**
 * Action to get iserver accounts
 * This action should be dispatched whenever iserver is connected.
 * TEN attempts are made to get iserver accounts
 */
export const getAccounts = ({ commit, dispatch, state }, { onSuccess = null, onError = null }) => {
    if (accountsFetcher != null) {
        accountsFetcher.stop();
    }
    retryAccounts = 0;
    accountsFetcher = new IntervalTimer(
        'iserverAccounts',
        {
            interval: 5000
        },
        () => doGetAccounts().then(res => {
            if (res && res.accounts && res.accounts.length) {
                console.debug("Iserver Info", res.serverInfo);
                console.debug("Iserver Accounts Received. Stopping polling...", res.accounts)
                accountsFetcher.stop();
                Metrics.instance().register({
                    source: 'iserver',
                    type: 'ui-accts-resp',
                    'value-int': ($date.now() - state.loginStartTime)
                });
                commit(types.SET_ISERVER_LOGIN_INFO, res);
                // TODO: Remove following mutations and expose getters based on SET_ISERVER_LOGIN_INFO
                commit(types.SET_ISERVER_INFO, res.serverInfo);
                commit(types.SET_ALLOW_FEATURES, res.allowFeatures);
                commit(types.SET_ISERVER_GROUPS, res.groups);
                commit(types.SET_ISERVER_PROFILES, res.profiles);
                commit(types.SET_ISERVER_ACCOUNTS, res.accounts);
                commit(types.SET_ISERVER_ACCOUNT_PROPS, res.acctProps);
                commit(types.SET_SELECTED_ACCOUNT, res.selectedAccount);
                commit(types.SET_ACCOUNT_ALIASES, res.aliases);
                commit(types.SET_CHART_PERIODS, res.chartPeriods);
                // set iserver accounts as picker accounts
                dispatch('setIserverAccountsAsPickerAccounts', {}, { root: true });
                safeCall('get-iserver-accounts', onSuccess, res);
            } else if (retryAccounts++ >= 9) {
                console.error(`Failed to fetch iserver accounts after ${retryAccounts} attempts. Stopping polling...`)
                accountsFetcher.stop();
                safeCall('get-iserver-accounts', onError, res);
            } else if (Object.keys(res).length === 0) {
                //reset accounts in this case
                commit(types.SET_ISERVER_ACCOUNTS, []);
                commit(types.SET_SELECTED_ACCOUNT, '');
                commit(types.SET_ISERVER_LOGIN_INFO, {});
            }
        }).catch(err => {
            if (retryAccounts++ >= 9) {
                console.error(`Failed to fetch iserver accounts after ${retryAccounts} attempts. Stopping polling...`)
                accountsFetcher.stop();
                safeCall('get-iserver-accounts', onError, err);
            }
        }),
        (e) => {
            console.error('Failed to dispatch request to get iserver accounts');
            safeCall('get-iserver-accounts', onError, e);
        }
    );
    accountsFetcher.start();
}

export const registerUnsolicitedWsHandlers = ({ commit, dispatch, state }, {}) => {
    realtime.setUnsolicitedHandlers([
        {
            topic: 'sts',
            type: 'AUTH',
            args: '',
            handlers:  [{
                onMessage: (e) => {
                    if (e && e.args) {
                        commit(types.SET_AUTH_STATUS, e.args);
                    }
                }
            }]
        },
        {
            topic: 'blt',
            type: 'BULLETIN',
            args: '',
            handlers:  [{
                onMessage: (e) => {
                    if (e && e.args) {
                        commit(types.SET_BULLETINS, e.args);
                    }
                }
            }]
        },
        {
            topic: 'ntf',
            type: 'NOTIFICATION',
            args: '',
            handlers:  [{
                onMessage: (e) => {
                    if (e && e.args) {
                        commit(types.SET_NOTIFICATIONS, e.args);
                    }
                }
            }]
        }
    ]);
}

/*** Iserver login ****/
/*
 * To remove since we are publishing ST  ourselves
 */
export const startSSODH = (ctx) => {
    console.debug('machine id', machineId);
    if (waitDH) {
        console.debug('Service ssodh,waiting. Request Not sent');
        return;
    }
    waitDH = true;
    ctx.commit(types.SET_ISERVER_LOGGING_IN_STATUS, true);
    if (ctx.rootState.common?.authTokenCreated) {
        retrySsodh = 0;
        setTimeout(() => ctx.dispatch('startIServerSSODH'), 1000);
        return;
    }
    if (retrySsodh++ > 2) {
        ctx.commit(types.SET_ISERVER_LOGGING_IN_STATUS, false);
        console.error('couldn\'t authenticate to brokerage');
        console.debug('service ssodh, retries exceeded. Returning.');
        return;
    }
    console.debug('service ssodh, retry:', retrySsodh, 'wait:', waitDH);
    authenticateSSODH(machineId, ctx.state.status.MAC)
        .then((res) => {
            console.debug('ssodh', res);
            ctx.commit(types.SET_ISERVER_SSODH, res);
            setTimeout(() => ctx.dispatch('startIServerSSODH'), 1000);
            retrySsodh = 0;
        })
        .catch((res, e) => {
            waitDH = false;
            ctx.commit(types.SET_ISERVER_LOGGING_IN_STATUS, false);
            console.error('ssodh service failure', res, e);
            setTimeout(() => ctx.dispatch('startSSODH'), 3000);
        });
}

export const startIServerSSODH = ({ dispatch, commit, state, getters, rootState }) => {
    console.debug('start iserver ssodh', state.ssoDH, 'compete', state.compete);
    if (!state.ssoDH && !state.ssoDH.username) {
        console.error('ssodh missing', state.ssoDH);
        commit(types.SET_ISERVER_LOGGING_IN_STATUS, false);
        waitDH = false;
        return;
    }
    if (getters.authenticated && !state.compete) {
        console.debug('start ssodh: authenticated');
        waitDH = false;
        commit(types.SET_ISERVER_LOGGING_IN_STATUS, false);
        return;
    }
    let locale = window.IB_LANG === 'en' ? 'en_US' : window.IB_LANG;
    if (!getters.isDev && !getters.isAlphaEnv) {
        locale = 'en_US';
    }
    api.ssoDHinit(state.ssoDH.username, state.compete, rootState.common?.authTokenCreated, locale).then((res) => {
        console.debug('ssodh init', res);
        if (res.wait) {
            console.debug('ssodh waiting to retry');
            setTimeout(() => dispatch('startIServerSSODH'), 3000);
            return;
        }
        if (res.authenticated || res.passed) {
            waitDH = false;
            commit(types.SET_ISERVER_LOGGING_IN_STATUS, false);
            dispatch('onIserverAuthStatus', res);
            return;
        }
        if (!state.ssoDH.K) {
            console.error('ssosh K is not defined', state.ssoDH.K);
            dispatch('retrySSODH');
            return;
        }
        const R = compute_sk(res.challenge, state.ssoDH.K);
        api.ssoDHresponse(R).then((res) => {
            waitDH = false;
            console.debug('sso dh response', res);
            if (res.passed || res.authenticated) {
                commit(types.SET_ISERVER_LOGGING_IN_STATUS, false);
                dispatch('onIserverAuthStatus', res);
            } else {
                console.error('ssodh response failed', res);
                setTimeout(() => dispatch('startSSODH'), 3000);
            }
        })
        .catch((res) => {
            console.error('failed ssodh response', httpError(res));
            dispatch('retrySSODH');
        });
    })
    .catch((res) => {
        console.error('failed ssodh init', httpError(res));
        dispatch('retrySSODH');
    });
}

export const compete = ({ commit, dispatch, state, rootGetters }, { compete = true}) => {
    if (rootGetters.hasSso) {
        commit(types.SET_COMPETE, compete);
        console.debug('compete',  compete);
        dispatch('startSSODH');
    } else {
        api.compete()
            .catch(res => this.sent = false);
        setTimeout(() => this.sent = false, 10000);
    }
}

export const login = ({ commit, dispatch, state, rootGetters }) => {
    console.debug('login iserver, has sso ', rootGetters.hasSso);
    commit(types.ISERVER_LOGIN_START_TIME);
    if (rootGetters.hasSso) {
        api.status()
            .then((res) => dispatch('onIserverAuthStatus', res))
            .catch((res) => dispatch('startSSODH'))
    } else {
        api.autologon()
            .then((res) => dispatch('onIserverAuthStatus', res))
            .catch((res) => setTimeout(() => dispatch('login'), 3000))
    }
}

export const onLoggedIn = (ctx, { authenticated, competing = undefined, connected }) => {
    console.debug('on status changed, authenticated', authenticated, 'competing', competing);
    if (authenticated) {
        retryReset('iserver-login');
        retryReset('iserver-ssodh');
    } else {
        ctx.dispatch('startSSODH');
    }
    ctx.dispatch('triggerEvent', {
        'name': 'ON_ISERVER_AUTHENTICATED',
        'component': null,
        'payload': {
            authenticated: authenticated,
            competing: competing
        }
    }, { root : true });
}

export const setClientStatus = (ctx, { id, value }) => api.setClientStatus(id, value);


/**********/


/**** Market Data ****/

/**
 * Subscribe market data for the given conids and fields
 */
export const getMarketdata = ({ commit, dispatch, state, rootGetters }, { conids, since=0, fields, backout=true, onSuccess, onError, useNewMD=false }) => {
    const realtime = rootGetters.realtime;
    if (realtime === undefined) {
        console.error('Transport mode unknown. Couldnt subscribe market data', conids);
        return;
    }

    /**
     * Currently, not all components are updated to use string-based conid. So, integer conids
     * are converted to string here
     */
    conids = conids.map(c => (c + ''));

    const onUpdates = data => {
        data = Array.isArray(data) ? data[0] : data;
        if (data?.conidEx) {
            /**
             * override conid value with conidEx.
             * This is done to support components which rely on conid value included in market data object
             * Must be removed once we retire 'conid' field and completely move to 'conidEx'
             */
            data.conid = data.conidEx;
            commit(types.SET_MARKET_DATA, { conid: data.conid, data });
        }
    }
    for (let i = 0; i < conids.length; i++) {
        const conid = conids[i];
        subscribedConids[conid] = (subscribedConids[conid] || 0) + 1;
        if (useNewMD) {
            if (realtime) { // send request via websockets
                mdApi.newSubscribeUpdates(
                    {
                        'conid': conid,
                        'tempo': 1000,
                        'fields': fields
                    },
                    {
                        onUpdates
                    }
                );
            } else { // send request via REST
                mdApi.getNewSnapshot({ conids: [ conid ], fields }).then(onUpdates);
            }
        } else {
            if (realtime) { // send request via websockets
                mdApi.subscribeUpdates(
                    {
                        'conid': conid,
                        'tempo': 1000,
                        'fields': fields,
                        backout
                    },
                    {
                        onUpdates
                    }
                );
            } else { // send request via REST
                mdApi.getSnapshot({ conids: [ conid ], fields, backout }).then(onUpdates);
            }
        }
    }
}

/**
 * Temporary method to get market data for order ticket *ONLY*
 * Method should be removed and order ticket should use getMarketdata() action once
 * onSuccess callback to order ticket is no longer required.
 */
export const getOrderTicketMarketdata = ({ commit, dispatch, state }, { conids, retries=0, fields, backout=true, onSuccess, onError }) => {
    if (!conids || !conids.length) {
        console.error("getOrderTicketMarketdata called without conid(s)");
        return;
    }
    if (retries > 0 && (retries == MAX_SNAPSHOT_RETRIES)) {
        if (onError) onError();
        return;
    }

    /**
     * Currently, not all components are updated to use string-based conid. So, integer conids
     * are converted to string here
     */
    conids = conids.map(c => (c + ''));

    const failedConids = [];
    const successfulConids = [];
    mdApi.getSnapshot({ conids, fields, backout })
        .then((arr) => {
            for (let i = 0; i < arr.length; i++) {
                const obj = arr[i];
                const conidex = obj.conidEx;
                const isBag = bagConidPattern.test(conidex);
                if (conidex) {
                    obj.conid = obj.conidEx;
                    commit(types.SET_MARKET_DATA, { conid: conidex, data: obj });
                }
                //6509 is always needed, especially for lite users
                if (conidex && (obj['6509'] || isBag)) {
                    successfulConids.push(conids[i]);
                } else {
                    failedConids.push(conids[i]);
                    if (!conidex) {
                        console.error("conid missing in get market data : ", obj);
                    }
                }
            }
            if (successfulConids.length > 0) {
                dispatch('getMarketdata', {
                    'conids': successfulConids,
                    'fields': fields,
                });
                if (failedConids.length === 0 && onSuccess) safeCall('get order ticket market data', onSuccess, null);
            }
            if (failedConids.length > 0) {
                // timeout serves as a buffer to let backend receive data from iserver after initial
                // request has been sent
                setTimeout(() => {
                    dispatch('getOrderTicketMarketdata', {
                        'conids': failedConids,
                        'fields': fields,
                        'retries': ++retries,
                        'onSuccess': onSuccess,
                        'onError': onError
                    });
                }, 1500);
            }
        })
        .catch((result) => {
            dispatch('getOrderTicketMarketdata', {
                'conids': conids,
                'fields': fields,
                'retries': ++retries,
                'onSuccess': onSuccess,
                'onError': onError
            });
        });

}

/**
 * Unsubscribes market data for the given conids only if no widgets are dependent on those conids
 * other than the one requesting the unsubscribe
 */
export const contingentUnsubscribeMarketdata = ({ commit, dispatch }, { conids }) => {
    const finalConids = [];
    for (let i = 0; i < conids.length; i++) {
        const conid = conids[i];
        if (!subscribedConids[conid]) {
            console.error('No active subscription for conid ', conid);
        } else if (subscribedConids[conid] > 1) {
            console.debug('More than one widget depends on the subscription. Unsubscribe skipped.', conid);
            subscribedConids[conid] -= 1;
        } else {
            finalConids.push(conid);
            subscribedConids[conid] = 0;
        }
    }
    if (finalConids.length) {
        mdApi.unsubscribeMarketData(finalConids);
    }
}

/*
 * Unsubscribes all the active market data subscriptions
 */
export const unsubscribeAllMarketData = ({ commit, dispatch, getters, rootGetters }, {}) => {
    window.ibrt && window.ibrt.clearSubscriptions('MD');
    subscribedConids = {};
    return new Promise((resolve, reject) => {
        if (rootGetters['abFeatures/showDirectMD']) {
            mdApi.newUnsubscribeAllMD()
                .then((res) => resolve(res))
                .catch((err) => reject(err));
        }
        if (getters.connected) {
            mdApi.unsubscribeAllMarketData()
                .then((res) => resolve(res))
                .catch((err) => reject(err));
        } else {
            resolve();
        }
    });
}

/*
 * Unsubscribes market data subscription for given conid
 */
export const unsubscribeMarketData = ({ commit, dispatch, getters, rootGetters }, { conid }) => {
    return mdApi.unsubscribeMarketData(conid);
}

export const startMarketDataPolling = ({ commit, dispatch, state }, {}) => {
    if (mdPollingHandler != null) {
        mdPollingHandler.stop();
    }
    mdPollingHandler = new IntervalTimer('subs md',
       {
            interval: POLLING_INTERVAL
       },
       () => mdApi.getMarketData()
            .then((res) => {
                if (res && Array.isArray(res)) {
                    res.forEach(md => {
                        md.conid = md.conidEx;
                        commit(types.SET_MARKET_DATA, { conid: md.conid, data: md });
                    })
                };
            })
            .catch((result) => {
                httpFailed('get-marketdata', commit, result)
            })
    );
    mdPollingHandler.start();
}

export const stopMarketDataPolling = ({ commit, dispatch, state }, {}) => {
    if (mdPollingHandler != null) {
        mdPollingHandler.stop();
    }
}

/**********/


/***** Orders *****/

export const subscribeOrders = ({ commit, dispatch, state, rootGetters }, { }) => {
    const realtime = rootGetters.realtime;
    if (realtime === undefined) {
        console.error('Transport mode unknown. Couldnt subscribe orders');
        return;
    } else if (realtime) { // send request via websockets
        if (!window.ibrt) {
            console.error('iserver: add rt real-time api missing');
            return;
        }
        // start polling to get initial snapshot
        getOrdersSnapshot({ commit });
        // register ws handler to handle order deltas
        window.ibrt.subscribeArgs('sor', 'ORDERS', {}, {
           onMessage: (e) => {
               const orders = (e || {}).args || [];
               commit(types.SET_ORDERS, orders);
           }
        });
    } else { // keep polling via REST
        if (ordersPollingHandler != null) ordersPollingHandler.stop();
        ordersPollingHandler = new IntervalTimer('subs orders',
           {
                interval: POLLING_INTERVAL
           },
           () => api.getOrders()
                .then((res) => {
                    if (res.orders) {
                        commit(types.SET_ORDERS, res.orders);
                    }
                })
                .catch((result) => {
                    httpFailed('get-orders', commit, result)
                })
        );
        ordersPollingHandler.start();
    }
}

export const getOrders = ({ commit, dispatch, state }, { force, onSuccess, onError }) => {
    api.getOrders(force)
        .then((res) => {
            safeCall('get-orders', onSuccess, res)
        })
        .catch((result) => {
            safeCall('get-orders', onError, result)
            httpFailed('get-orders', commit, result)
        });
}

/**
 * Forces backend to resend Live orders request to iserver by using force=true flag
 * and then polls orders till snapshot is received
 */
export const forceFetchOrdersSnapshot = ({ commit, dispatch, state }, {}) => {
    api.getOrders(true /* force */)
        .then((res) => {
            getOrdersSnapshot({ commit });
        })
        .catch((result) => {
            httpFailed('get-orders', commit, result)
        });
}

export const unsubscribeOrders = ({ commit, dispatch, state, rootGetters }, {}) => {
    if ((rootGetters.realtime === true) && window.ibrt) {
        window.ibrt.subscribeArgs('uor', 'ORDERS', {}, {
            onMessage: (e) => {}
        });
        window.ibrt.clearSubscriptions('ORDERS');
    }
    if (ordersPollingHandler != null) {
        ordersPollingHandler.stop();
    }
}

export const stopOrdersPolling = ({ commit, dispatch, state, rootGetters }, {}) => {
    if (ordersPollingHandler != null) {
        ordersPollingHandler.stop();
    }
}

/***********/

/***** PNL *****/

export const subscribePnl = ({ commit, dispatch, state, rootGetters }, { }) => {
    const realtime = rootGetters.realtime;
    if (realtime === undefined) {
        console.error('Transport mode unknown. Couldnt subscribe pnl');
        return;
    } else if (realtime) { // send request via websockets
        if (!window.ibrt) {
            console.error('iserver: add rt real-time api missing');
            return;
        }
        window.ibrt.subscribeArgs('spl', 'PNL', {}, {
           onMessage: (e) => {
               if (e && e.args) {
                   commit(types.SET_UPNL, e.args);
               }
           }
        });
    } else { // keep polling via REST
        if (pnlPollingHandler != null) pnlPollingHandler.stop();
        pnlPollingHandler = new IntervalTimer('subs pnl',
           {
                interval: POLLING_INTERVAL
           },
           () => api.getPnl()
                .then((res) => {
                    if (res.upnl) {
                        commit(types.SET_UPNL, res.upnl);
                    }
                })
                .catch((result) => {
                    if (onError) onError(result.response);
                    httpFailed('get-pnl', commit, result)
                })
        );
        pnlPollingHandler.start();
    }
}

export const getPnl = ({ commit, dispatch, state }, {}) => {
    api.getPnl()
        .then((res) => {
            safeCall('get-pnl', onSuccess, res)
        })
        .catch((result) => {
            if (onError) onError(result.response);
            httpFailed('get-pnl', commit, result)
        });
}

export const unsubscribePnl = ({ commit, dispatch, state, rootGetters }, {}) => {
    if ((rootGetters.realtime === true) && window.ibrt) {
        window.ibrt.subscribeArgs('upl', 'PNL', {}, {
            onMessage: (e) => {}
        });
        window.ibrt.clearSubscriptions('PNL');
    }
    if (pnlPollingHandler != null) {
        pnlPollingHandler.stop();
    }
}

export const stopPnlPolling = ({ commit, dispatch, state, rootGetters }, {}) => {
    if (pnlPollingHandler != null) {
        pnlPollingHandler.stop();
    }
}

/***********/

/***** Auth Status *****/

export const startAuthStatusPolling = ({ commit, dispatch, state }, { onError }) => {
    if (authStatusPollingHandler != null) {
        authStatusPollingHandler.stop();
    }
    authStatusPollingHandler = new IntervalTimer('subs md',
       {
            interval: POLLING_INTERVAL
       },
       () => api.status()
            .then((res) => {
                if (res) {
                    commit(types.SET_AUTH_STATUS, res);
                };
            })
            .catch((result) => {
                if (onError) onError(result.response);
                httpFailed('get-auth-status', commit, result)
            })
    );
    authStatusPollingHandler.start();
}

export const stopAuthStatusPolling = ({ commit, dispatch, state }, {}) => {
    if (authStatusPollingHandler != null) {
        authStatusPollingHandler.stop();
    }
}

/***********/

export const getBulletins = ({ commit, dispatch, state }, { onSuccess, onError }) => {
    api.getBulletins()
        .then((res) => {
            safeCall('get-bulletins', onSuccess, res);
            commit(types.SET_BULLETINS, res);
        })
        .catch((result) => {
            if (onError) onError(result.response);
            httpFailed('get-bulletins', commit, result)
        });
}

export const markBulletinsAsRead = ({ commit, dispatch, state }, { ids, onSuccess, onError }) => {
    if (!ids) {
        safeCall('mark-bulletin-as-read', onError, null);
    }
    commit(types.MARK_BULLETINS_AS_READ, ids);
    safeCall('mark-bulletin-as-read', onSuccess, null);
}

export const getBriefingArticle = ({ commit, dispatch, state }, { el, retries = 0, onSuccess = null, onError = null }) => {
    if (retries > 2) {
        progressEnd(el);
        safeCall('get briefing article', onError, {});
        return;
    }
    if (retries === 0) {
        progressStart(el);
    }
    api.getBriefingArticle()
        .then((res) => {
            if (!res || res.error) {
                dispatch('getBriefingArticle', {
                    el, 'retries': ++retries, onSuccess, onError
                });
            } else {
                progressEnd(el);
                commit(types.SET_BRIEFING_ARTICLE, res);
                safeCall('get briefing article', onSuccess, res);
            }
        })
        .catch((result) => {
            dispatch('getBriefingArticle', {
                el, 'retries': ++retries, onSuccess, onError
            });
        });
}

export const performAccountSwitch = ({ state, commit, dispatch }, { account, onSuccess, onError }) => {

    // clear previous pnl, orders and market data
    commit(types.CLEAR_ISERVER_DATA, {}); // clears market data, orders, pnl data

    // set currently seelcted account to undefined
    commit(types.SET_SELECTED_ACCOUNT, undefined);

    api.setAccount(account)
        .then((arr) => {
            commit(types.SET_SELECTED_ACCOUNT, account);
            console.debug(`Account Switch: Account switched to ${account}`);
            if (onSuccess) safeCall('account switch', onSuccess, null);
        })
        .catch((result) => {
            // Switching failed
            if (onError) safeCall('account switch', onError, null);
        });
}

export const searchBySym = ({ commit, dispatch, state }, { el, retries = 0, sym, secType, referrer, onSuccess = null, onError = null }) => {
    if (retries > 2) {
        progressEnd(el);
        safeCall('search by symbol', onError, {});
        return;
    }
    progressStart(el);
    api.searchBySym(sym, secType, referrer)
        .then((res) => {
            progressEnd(el);
            if (!res || res.error) {
                safeCall('search by symbol', onError, res);
            } else {
                safeCall('search by symbol', onSuccess, res);
            }
        })
        .catch((result) => {
            progressEnd(el);
            dispatch('searchBySym', {
                el, sym, secType, referrer, 'retries': ++retries, onSuccess, onError
            });
        });
}

export const getLinks = ({ commit, dispatch, state }, { linkTypes }) => {
    return new Promise((resolve, reject) => {
        api.getLinks(linkTypes)
            .then((res) => {
                commit(types.SET_LINKS, res);
                resolve(res);
            })
            .catch((result) => {
                reject(result);
            });
    });
}

export const getRegsnapshot = ({ commit, dispatch, state, rootGetters }, { el, conid, persist }) => {
    progressStart(el);
    const useMdFarmRegsnapshot = rootGetters['abFeatures/useMdFarmRegsnapshot'];
    const regSnapshotApi = useMdFarmRegsnapshot ? mdApi : api;
    return new Promise((resolve, reject) => {
        regSnapshotApi.getRegsnapshot(conid)
            .then((res) => {
                // generate notification to display snapshot warning/fee message to user
                if (useMdFarmRegsnapshot && ((++regsnapshotRequestCount % SNAPSHOT_FEE_ALERT_LIMIT) == 1)) {
                    commit(types.SET_NOTIFICATIONS, [
                            (regsnapshotRequestCount > SNAPSHOT_EXCESSIVE_USAGE_LIMIT)
                            ? SNAPSHOT_EXCESSIVE_USAGE_WARNING
                            : SNAPSHOT_FEE_ALERT
                        ]
                    );
                }
                progressEnd(el);
                // snapshot_time (7066) has to be present in the response
                if (!res || res.error || !res['7066']) {
                    reject(res);
                } else {
                    resolve(res);
                    if (persist) {
                        commit(types.SET_REGSNAPSHOT, res);
                    }
                }
            })
            .catch((result) => {
                progressEnd(el);
                reject(result);
            });
    });
}

export const clearRegsnapshots = ({ commit, dispatch, state }, {}) => {
    commit(types.CLEAR_REGSNAPSHOTS);
}

export const setOEMode = ({ commit, dispatch, state }, { conid, isUnsubscribe }) => {
    return new Promise((resolve, reject) => {
        if (!conid || conid === 'undefined') {
            reject('Invalid conid');
            return;
        }
        api.setOEMode(conid, isUnsubscribe)
            .then(res => {
                resolve(res.result);
            })
            .catch(e => {
                reject(httpError(e));
            });
        });
}

export const getDebugPnl = ({ commit, dispatch, state }, { conid, type, account = '' }) => {
    return new Promise((resolve, reject) => {
        api.getDebugPnl({ conid, type, account })
            .then(res => {
                resolve(res);
            })
            .catch(e => {
                reject(httpError(e));
            });
        });
}

export const getCurrencyPairs = ({ commit, dispatch, state }, { currency }) => {
    api.getCurrencyPairs(currency)
        .then((res) => {
            commit(types.CURRENCY_PAIRS, res);
        })
        .catch((result) => {
            httpFailed('currency pairs', commit, result)
        });
}

export const getContractInfo = ({ commit, dispatch, state }, { conid }) => {
    return new Promise((resolve, reject) => {
        if (!conid) {
            reject(`Invalid conid: ${conid}`)
        } else if(bagConidPattern.test(conid)) { // contract API request doesn't support combos
            resolve({});
        } else if (state.contractInfo[conid]) {
            // serve data which was already fetched
            resolve(state.contractInfo[conid]);
        } else {
            // fetch contract info
            api.getContractInfo(conid)
                .then(res => {
                    commit(types.SET_CONTRACT_INFO, res);
                    resolve(res);
                })
                .catch(e => {
                    reject(httpError(e));
                });
        }
    });
}

/**
 * Action to subscribe to trades
 * Subscription will not be sent if a subscription is already active
 * Including force flag will send trades subscription even if its already active
 */
export const subscribeTrades = ({ commit, dispatch, state, rootGetters }, { force }) => {
    if (!force && tradesSubscriptionActive) {
        return;
    }
    const realtime = rootGetters.realtime;
    if (realtime === undefined) {
        console.error('Transport mode unknown. Couldnt subscribe trades');
        return;
    } else if (realtime) { // send request via websockets
        if (!window.ibrt) {
            console.error('iserver: add rt real-time api missing');
            return;
        }
        tradesSubscriptionActive = true;
        // register ws handler to handle trades
        window.ibrt.subscribeArgs('str', 'TRADES', {}, {
           onMessage: (e) => {
               const trades = e?.args || [];
               commit(types.SET_TRADES, trades);
           }
        });
    } else { // keep polling via REST
        if (tradesPollingHandler != null) tradesPollingHandler.stop();
        tradesPollingHandler = new IntervalTimer('subs trades',
           {
                interval: POLLING_INTERVAL
           },
           () => api.getTrades()
                .then((res) => {
                    commit(types.SET_TRADES, res || []);
                })
                .catch((result) => {
                    httpFailed('get-trades', commit, result)
                })
        );
        tradesSubscriptionActive = true;
        tradesPollingHandler.start();
    }
}

export const unsubscribeTrades = ({ commit, dispatch, state, rootGetters }, {}) => {
    if (!tradesSubscriptionActive) {
        return;
    }
    if ((rootGetters.realtime === true) && window.ibrt) {
        window.ibrt.subscribeArgs('utr', 'TRADES', {}, {
            onMessage: (e) => {}
        });
        window.ibrt.clearSubscriptions('TRADES');
    }
    if (tradesPollingHandler != null) {
        tradesPollingHandler.stop();
    }
    tradesSubscriptionActive = false;
}

export const setCcyChartPair = ({ commit, state }, data) => {
    commit(types.CCY_CHART_PAIR, data);
}

export const setWatchlistMap = ({ commit,  state }, { id, watchlistConids }) => {
    commit(types.SET_WATCHLISTMAP, { id, watchlistConids });
}

export const fetchWatchlist = ({ commit, dispatch, state }, { id, onSuccess, onError }) => {
    if (!id) return;
    if (state.watchlistMap[id] && state.watchlistMap[id].conids){
        return state.watchlistMap[id];
    } else {
        api.getWatchlist(id).then((res) => {
            let instr = (res && res.instruments);
            if (instr) {
                const watchlistConids = {
                    conids: instr.map(instrument => instrument.conid)
                };
                dispatch('setWatchlistMap', { id, watchlistConids });
            }
            safeCall('get-watchlist', onSuccess, res);
        })
        .catch((result) => {
            safeCall('get-watchlist', onError, result);
            httpFailed('get watchlist', commit, result);
        });
    }
}

export const getMDColumns = ({ commit, dispatch, state }, { data, onSuccess, onError }) => {
    api.getMDColumns(data)
        .then((res) => {
            safeCall('get-market-data-fields', onSuccess, res);
        })
        .catch((result) => {
            safeCall('get-market-data-fields', onError, result);
            httpFailed("get market data fields", commit, result);
        });
}

export const getPFMDColumns = ({ commit, dispatch, state }, { data }) => {
    return new Promise((resolve, reject) => {
        api.getPFMDColumns(data?.SELECTED?.join(','))
            .then((res) => {
                resolve(res);
            })
            .catch((result) => {
                httpFailed("get portfolio market data fields", commit, result);
                reject(result);
            });
    });
}

export const getWLMDColumns = ({ commit, dispatch, state }, { data }) => {
    return new Promise((resolve, reject) => {
        api.getWLMDColumns(data?.SELECTED?.join(','))
            .then((res) => {
                resolve(res);
            })
            .catch((result) => {
                httpFailed("get watchlist market data fields", commit, result);
                reject(result);
            });
    });
}

export const getAvailableAlerts = ({ commit, dispatch, state }, { accountId }) => {
    return new Promise((resolve, reject) => {
        api.getAvailableAlerts(accountId)
            .then(res => {
                commit(types.SET_AVAILABLE_ALERTS, res);
                resolve(res);
            })
            .catch(err => {
                httpFailed('get available alerts failed', commit, err);
                reject(err);
            });
    });
}

export const getPriceAlerts = ({ commit, dispatch, state }, { alertsIds } ) => {
    state.loadedPriceAlertsData = false;
    const getAlertDetails = (id) => {
        return new Promise((resolve, reject) => {
            api.getAlertDetails(id)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    httpFailed('get alerts details failed', commit, err);
                    reject(err);
                });
        })
    }

    const promises = [];

    alertsIds.map((alert) => {
        promises.push(getAlertDetails(alert))
    });

    return new Promise((resolve, reject) => {
        Promise.all(promises)
            .then(response => {
                //-- Price alerts have only 1 condition  and condition_type is 1 
                let priceAlerts = response.filter(alert => {
                    return alert.condition_size === 1 && alert.conditions[0].condition_type === 1
                });
                priceAlerts = priceAlerts.map(alert => ({
                    ...alert,
                    conid: parseInt(alert.conditions[0].conidex),
                    condition_value: alert.conditions[0].condition_value
                }));

                //-- set the Order Rules for each conid in Price Alerts
                priceAlerts.forEach((alert, idx) => {
                    //-- we always use 'buy' for price alerts
                    let key = alert.conid + 'buy';

                    //-- first check stored OrderRules for this key
                    if (state.orderRules?.key) {
                        formatAlertByRules(state.orderRules.key, alert);

                        //--resolve the price alerts at the end of the forEach loop
                        if (idx === (priceAlerts.length -1)) {
                            commit(types.SET_PRICE_ALERTS, priceAlerts);
                            resolve(priceAlerts);
                        }
                    } else {
                        dispatch('getOrderRules',
                        {
                            conid: alert.conid + '',
                            isBuy: true
                        })
                        .then(res => {
                            formatAlertByRules(res, alert);

                            //--resolve the price alerts at the end of the forEach loop
                            if (idx === (priceAlerts.length -1)) {
                                commit(types.SET_PRICE_ALERTS, priceAlerts);
                                resolve(priceAlerts);
                            }
                        })
                        .catch(e => {
                            console.error('Failed to get the order rules' + e);
                        });
                    }
                });
            })
            .catch(err => {
                console.log(`Error in executing ${error}`)
                reject(err);
            })
    });
}

export const deleteAlert = ({ commit, dispatch, state }, { accountId, alertId } ) => {
    return new Promise((resolve, reject) => {
        api.deleteAlert(accountId, alertId)
            .then(res => {
                resolve(res)
            })
            .catch(err => {
                httpFailed('delete alert failed', commit, err);
                reject(err);
            });
        });
}

export const activateAlert = ({ commit, dispatch, state }, { accountId, alertId, alertActive }) => {
    return new Promise((resolve, reject) => {
        api.activateAlert(accountId, { alertId: alertId, alertActive: alertActive })
            .then(res => {
                resolve(res);
            })
            .catch(err => {
                httpFailed('activate alert failed', commit, err);
                reject(err);
            });
    });
}

export const createOrModifyAlert = ({ commit, dispatch, state }, { accountId, body } ) => {
    return new Promise((resolve, reject) => {
        api.createOrModifyAlert(accountId, body)
            .then(res => {
                resolve(res);
            })
            .catch(err => {
                httpFailed('create alert failed', commit, err);
                reject(err);
            });
    });
}

export const setPriceAlertsDeliveryOptions = ({ commit, state }, data ) => {
    commit(types.SET_PRICE_ALERTS_DELIVERY_OPTIONS, data);
}

export const resetAvailableAlerts = ({ commit, state }) => {
    state.availableAlerts = [];
}

export const getMtaAlerts = ({ commit, dispatch, state }) => {
    return new Promise((resolve, reject) => {
        api.getMtaAlert()
            .then(res => {
                commit(types.SET_MTA_ALERTS, res);
                resolve(res);
            })
            .catch(err => {
                httpFailed('get mta alerts failed', commit, err);
                reject(err);
            });
    });
}

export const setUserTimeZone = ({ commit, state }, data ) => {
    commit(types.SET_USER_TIMEZONE, data);
}

export const onAuthStatus = ({ commit, state, dispatch }, data ) => {
    if (!data) {
        return;
    }
    if (state.status.backendDown && !data.backendDown) {
        // backend was down but is now up again, so we re-authenticate iserver session
        dispatch('login', {});
    } else if (data.backendDown) {
        // when the backend goes down, stop polling orders, pnl & market data
        dispatch('startMarketDataPolling', {});
        dispatch('stopPnlPolling', {});
        dispatch('stopOrdersPolling', {});
    }
    commit(types.SET_AUTH_STATUS, data || {});
}

/**
 * Data consist of following entries
 * conid, exchange, isBuy, isClose, hasSecondary, limitPrice
 */
export const getOrderRules = ({ commit, dispatch, state }, data) => {
    return new Promise((resolve, reject) => {
        api.getOrderRules(data)
            .then((res) => {
                resolve(res);
            })
            .catch((err) => {
                reject(httpError(err));
                httpFailed("get order rules", commit, err);
            });
    });
}

/**
 * Get Crypto Clarification text, requires contract info for clarification type
 */
export const getCryptoClarification = ({ commit, dispatch, state }, {conid, origin, time, tif, isBuy, orderType}) => {
    return new Promise((resolve, reject) => {
        dispatch('getContractInfo', { conid: conid })
            .then(res => {
                return api.getCryptoClarification(res?.contract_clarification_type,
                    origin, time, tif, isBuy,conid, orderType)
            })
            .then(res => {
                let ret = res?.text;
                if (!ret) {
                    reject('Empty Response');
                }
                const linkRegex = new RegExp("<a.*/a>");
                if (linkRegex.test(ret)) {
                    const urlRegex = /href="([^"]*)/;
                    const url = ret.match(urlRegex)[0];
                    const tag = url.split("/").pop();
                    let faqLink = ' [' + ('More Information') + '](javascript:onebar.openFAQ\\({tag:' + JSON.stringify(tag) + '}\\))';
                    faqLink = $markdown.toHTML(faqLink).replace('<p>','<span>').replace('</p>', '</span>');
                    ret = ret.replace(linkRegex, "");
                    ret = ret + faqLink;
                    resolve(ret);
                } else {
                    resolve(ret);
                }
            })
            .catch(err => {
                reject(err);
            });
    });
}

export const getWatchlist = ({ commit, dispatch, state }, {  el, id, explore, onSuccess, onError }) => {
    if (!id) return;
    progressStart(el);
    api.getWatchlist(id).then((res) => {
        let instr = (res && res.instruments);
        if (instr) {
            commit('SET_WL_INSTR', { id, instr });
            const watchlistConids = {
                conids: instr.map(instrument => instrument.conid)
            };
            dispatch('setWatchlistMap', { id, watchlistConids });
        }
        safeCall('get watchlist', onSuccess, res);
        progressEnd(el);
    })
    .catch((result) => {
        progressEnd(el);
        safeCall('get watchlist', onError, result);
        httpFailed('get watchlist', commit, result);
    });
}

export const getAllWatchlists = ({ commit, dispatch, state }, {  el, onSuccess, onError, SC }) => {
    progressStart(el);
    return api.getAllWatchlists({SC}).then((res) => {
        const data = (res && res.data) || {};
        commit('SET_SYSTEM_WLS',  data.system_lists || []);
        commit('SET_USER_WLS', data.user_lists || []);
        safeCall('get all watchlist', onSuccess, {});
        progressEnd(el);
    })
    .catch((result) => {
        progressEnd(el);
        safeCall('get all watchlist', onError, getHttpErrorReason(result));
        httpFailed('get all watchlist', commit, result);
    });
}

export const saveWatchlist = ({ commit, dispatch, state }, { wl, el, onSuccess, onError }) => {
    if (el) {
        progressStart(el);
    }
    api.saveWatchlist(wl)
        .then(res => {
            wl.id = res.id;
            wl.name = res.name;
            wl.readOnly = res.readOnly;
            wl.hash = res.hash;
            wl.type = 'watchlist';
            wl.modified = new Date().getTime();
            commit('SAVE_WATCHLIST', wl);
            safeCall('add watchlist successfully', onSuccess, wl);
            if (el) {
                progressEnd(el);
            }
        }).catch(res => {
            safeCall('add watchlist failed', onError, res);
            if (el) {
                progressEnd(el);
            }
        });
}

export const appendToWatchlist = ({ commit, dispatch, state }, { id, symbols, onSuccess, onError }) => {
    dispatch('getWatchlist', { id: id, onSuccess: (wl) => {
        console.debug('found wl', wl);
        // Reducing Params of instruments to decrease Payload Size
        const instruments = ((wl && wl.instruments) || []).map( r => {
            return {
                C: r.C,
                conid: r.conid
            }
        });
        console.debug('with instruments', instruments);
        // prevent addition of already existing symbols
        for (let i in symbols) {
            const sym = symbols[i];
            if (!instruments.find(o => o.C == sym.conid)) {
                instruments.push({ C: sym.conid });
            }
        }
        const obj = {
            id: wl.id,
            name: wl.name,
            'rows': instruments
        };
        dispatch('saveWatchlist', { wl: obj, onSuccess: (e) => {
            safeCall('append-save-wl', onSuccess, obj);
        }, onError: (e) => {
            safeCall('pass-error-wl', onError, httpError(e));
        }});
    }});
}

export const updateAuthStatus = ({ commit, state, dispatch }, data ) => {
    if (!data) {
        return;
    }
    commit(types.SET_AUTH_STATUS, data || {});
}

export const getHistorydata = ({ commit, dispatch, state, rootGetters }, { conid, period, bar, outsideRth, startTime, direction, onSuccess, onError }) => {
    const realtime = rootGetters.realtime;
    const useDirectHMDS = rootGetters['abFeatures/showDirectHMDS'];

    conid  = conid + '';

    const onUpdates = data => {
        safeCall('get-history-data', onSuccess, data);
    }

    const onHistoryDataError = data => {
        safeCall('get-history-data', onError, data);
    }

    if (realtime) { // send request via websockets
        if (useDirectHMDS) {
            mdApi.getHistoryDataDirectHMDS(
                {
                    'conid': conid,
                    'period': period,
                    'bar': bar,
                    'outsideRth': outsideRth,
                    'startTime': startTime,
                    'direction': direction
                },
                {
                    onUpdates,
                    onHistoryDataError
                }
            );
        } else {
            mdApi.getHistoryDataIserver(
                {
                    'conid': conid,
                    'period': period,
                    'bar': bar,
                    'outsideRth': outsideRth,
                    'startTime': startTime,
                    'direction': direction
                },
                {
                    onUpdates,
                    onHistoryDataError
                }
            );
        }
    } else { //send request via api
        const historyApi = useDirectHMDS ? hmdsApi : api;
        historyApi.getPriceHistory(conid, period, '', '', bar, outsideRth, startTime, direction)
            .then((res) => {
                safeCall('get-history-data', onSuccess, res);
            })
            .catch((result) => {
                safeCall('get-history-data', onError, result);
            });
    }
}
